import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type MetaFunction, Link } from '@remix-run/react'
import { ArrowLeftIcon, CopyIcon, MailIcon } from 'lucide-react'
import { toast } from 'sonner'
import { useCopyToClipboard } from 'usehooks-ts'
import { Button } from '#app/components/ui/button'

export const handle: SEOHandle = {
	getSitemapEntries: () => [{ route: '/contact' }],
}

export const meta: MetaFunction = () => [
	{ title: `Contact | Erda` },
	{
		name: 'description',
		content: `Need support, spotted a bug, or have a feature request? We're here to help! Please reach out to us with any questions or concerns`,
	},
	{
		name: 'keywords',
		content: 'contact, get in touch, support, bug report, feature request',
	},
	{ name: 'og:title', content: `Contact | Erda` },
	{ name: 'og:type', content: 'website' },
	{ name: 'og:url', content: 'https://erda.app' },
	{
		name: 'og:image',
		content: `https://erda.app/image/contact.png`,
	},
	{ name: 'og:card', content: 'summary_large_image' },
	{ name: 'og:creator', content: '@erda_app' },
	{ name: 'og:site', content: 'https://erda.app' },
	{
		name: 'og:description',
		content: `Need support, spotted a bug, or have a feature request? We're here to help! Please reach out to us with any questions or concerns`,
	},
	{
		name: 'twitter:image',
		content: `https://erda.app/image/contact.png`,
	},
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:creator', content: '@erda_app' },
	{ name: 'twitter:title', content: `Contact | Erda` },
	{
		name: 'twitter:description',
		content: `Need support, spotted a bug, or have a feature request? We're here to help! Please reach out to us with any questions or concerns`,
	},
]

export default function Contact() {
	const [, copy] = useCopyToClipboard()

	const copyEmail = (email: string) => {
		copy(email)
		toast.success('Email copied to clipboard')
	}

	return (
		<>
			<h1 className="sr-only">Contact</h1>
			<Button
				data-vaul-no-drag
				variant="link"
				className="h-auto w-fit self-center"
				asChild
			>
				<Link to="/info">
					<ArrowLeftIcon className="h-4 w-4" /> Info hub
				</Link>
			</Button>
			<div className="space-y-6 divide-y">
				<div className="group space-y-2 pt-6">
					<h2>Need support?</h2>
					<p className="text-sm">
						We're here to help! Please reach out to us with any questions or
						concerns.
					</p>

					<div className="flex gap-3">
						<Button variant="link" className="gap-2">
							<MailIcon className="h-4 w-4" />
							<Link to="mailto:support@erda.app">support@erda.app</Link>
						</Button>
						<Button
							variant="link"
							onClick={() => copyEmail('support@erda.app')}
							className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100"
						>
							<CopyIcon className="h-3.5 w-3.5" />
							Copy email
						</Button>
					</div>
				</div>
				<div className="group space-y-2 pt-6">
					<h2>Found a bug?</h2>
					<p className="text-sm">
						Please let us know! We appreciate your help in making Erda better.
					</p>
					<div className="flex gap-3">
						<Button variant="link" className="gap-2">
							<MailIcon className="h-4 w-4" />
							<Link to="mailto:bug-report@erda.app">bug-report@erda.app</Link>
						</Button>
						<Button
							variant="link"
							onClick={() => copyEmail('bug-report@erda.app')}
							className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100"
						>
							<CopyIcon className="h-3.5 w-3.5" />
							Copy email
						</Button>
					</div>
				</div>
				<div className="group space-y-2 pt-6">
					<h2>Have a feature request?</h2>
					<p className="text-sm">
						We'd love to hear your ideas! Please share them with us.
					</p>
					<div className="flex gap-3">
						<Button variant="link" className="gap-2">
							<MailIcon className="h-4 w-4" />
							<Link to="mailto:feature-request@erda.app">
								feature-request@erda.app
							</Link>
						</Button>
						<Button
							variant="link"
							onClick={() => copyEmail('feature-request@erda.app')}
							className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100"
						>
							<CopyIcon className="h-3.5 w-3.5" />
							Copy email
						</Button>
					</div>
				</div>
			</div>
		</>
	)
}
